import { useCallback, useEffect, useRef } from "react";

import { useApolloClient } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";

import {
  GetOrderForPickingDocument,
  GetOrderForPickingQuery,
} from "app/queries/orderSummary/orderSummary.generated";
import {
  useAppBackgroundEffectsStore,
  SummaryOfNextOrderForPicking,
} from "app/stores/useAppBackgroundEffectsStore";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { usePickingStore } from "flows/Picking/stores/usePickingStore";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { isNotNullNorUndefined } from "utils/tsHelpers";

const POLLING_BREAK_START_HOUR = 2; // 2 AM
const POLLING_BREAK_STOP_HOUR = 6; // 6 AM
const DEFAULT_POLLING_INTERVAL_MS = 3000;
const SKIPPED_EVENT_THROTTLE_MS = 10 * 60 * 1000;

const isInPollingHours = () => {
  const hour = new Date().getHours();
  return !(hour >= POLLING_BREAK_START_HOUR && hour < POLLING_BREAK_STOP_HOUR);
};

export type SendSegmentTrackEvent = ReturnType<typeof useAnalytics>["sendSegmentTrackEvent"];

/**
 * Single entry point for reacting to a `getOrderForPicking` response. Used by
 * both the background polling loop and PickingIndex's fallback direct query so
 * the `offered` event and polling cache stay in sync regardless of which path
 * discovered the order first.
 *
 * Emits `orderStateUpdated:offered` only when the response is a NEW order:
 * different from the cached one AND not the order the picker is currently
 * working on (picking-service returns the in-progress order on subsequent
 * polls for resumption — without that second guard, a duplicate `offered`
 * fires after `picked` and produces negative reaction times downstream).
 *
 * The order the picker just packed is treated as no order at all, so a poll
 * sent before the pack can't bring it back. The first response about anything
 * else shows picking-service has moved on, so the guard is dropped then: an
 * order that is reopened later is offered again as usual.
 */
export function processNextOrderForPickingResponse(
  response: SummaryOfNextOrderForPicking | null,
  sendSegmentTrackEvent: SendSegmentTrackEvent,
): void {
  const { summaryOfNextOrderForPicking, lastPackedOrderId, setAppBackgroundEffectsStore } =
    useAppBackgroundEffectsStore.getState();
  const currentlyPickingOrderId = usePickingStore.getState().order?.id ?? null;
  const isJustPackedOrder = isNotNullNorUndefined(response) && response.id === lastPackedOrderId;
  const newSummary = isJustPackedOrder ? null : response;

  if (
    isNotNullNorUndefined(newSummary) &&
    newSummary.id !== summaryOfNextOrderForPicking?.id &&
    newSummary.id !== currentlyPickingOrderId
  ) {
    sendSegmentTrackEvent("orderStateUpdated", {
      state: "offered",
      origin: "PICKING_SCREEN",
      order_number: newSummary.number,
      order_id: newSummary.id,
    });
  }

  setAppBackgroundEffectsStore({
    summaryOfNextOrderForPicking: newSummary,
    lastPackedOrderId: isJustPackedOrder ? lastPackedOrderId : null,
  });
}

export function useFetchNextOrder() {
  const client = useApolloClient();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { isAuthenticated } = useAuth0();

  // Prevent overlapping fetches
  const isFetchingRef = useRef(false);
  const lastSkippedLogRef = useRef(0);

  const fetchNextOrder = useCallback(async () => {
    try {
      const { data } = await client.query<GetOrderForPickingQuery>({
        query: GetOrderForPickingDocument,
        fetchPolicy: "network-only",
        errorPolicy: "all",
      });

      const summary = data?.getOrderForPicking?.orderSummary;
      const newSummary: SummaryOfNextOrderForPicking | null = summary
        ? { id: summary.id, number: summary.number ?? null }
        : null;

      processNextOrderForPickingResponse(newSummary, sendSegmentTrackEvent);
    } catch (error) {
      datadogRum.addError(error, {
        message: "Error fetching next order for picking",
        fetch_time: new Date().toISOString(),
      });
      useAppBackgroundEffectsStore
        .getState()
        .setAppBackgroundEffectsStore({ summaryOfNextOrderForPicking: null });
    }
  }, [client, sendSegmentTrackEvent]);

  useEffect(() => {
    const poll = async () => {
      try {
        if (isFetchingRef.current) return;

        const { isIdentified } = useEmployeeStore.getState();
        const inPollingHours = isInPollingHours();
        const shouldPoll = isAuthenticated && isIdentified && inPollingHours;

        if (shouldPoll) {
          isFetchingRef.current = true;
          try {
            await fetchNextOrder();
          } finally {
            isFetchingRef.current = false;
          }
        } else if (inPollingHours && !shouldPoll) {
          const now = Date.now();
          if (now - lastSkippedLogRef.current > SKIPPED_EVENT_THROTTLE_MS) {
            datadogRum.addAction("NextOrderPollingSkipped", {
              shouldPoll,
              isAuthenticated,
              isIdentified,
              inPollingHours,
              time: new Date().toISOString(),
            });
            lastSkippedLogRef.current = now;
          }
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: "Unexpected error in NextOrder polling",
        });
      }
    };

    // Run immediately once
    poll();

    // Set up continuous polling
    const intervalId = setInterval(poll, DEFAULT_POLLING_INTERVAL_MS);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [fetchNextOrder, isAuthenticated]);
}
